import { CommonModule, DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { RialToTomanPipe } from '@shared/pipes/rial-to-toman.pipe';
import {
 afterNextRender,
 Component,
 DestroyRef,
 effect,
 forwardRef,
 Inject,
 inject,
 makeStateKey,
 OnInit,
 Optional,
 PLATFORM_ID,
 signal,
 TransferState,
} from '@angular/core';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { HotelRepository } from '@core/services/repositories/hotel.repository';
import { ActivatedRoute } from '@angular/router';
import { cdnUrl } from '../../app.module';
import { DesktopSearchboxComponent } from '../search-result/components/desktop-searchbox/desktop-searchbox.component';
import { AutocompleteService } from '@shared/services/autocomplete.service';
import { DatepickerService } from '@shared/services/datepicker.service';
import { SearchBoxService } from '@shared/services/serach-box.service';
import { PopularDestinationComponent } from './popular-destination/popular-destination.component';
import { SimilarHotelsCardComponent } from '../accommodation-info/components/similar-hotels-card/similar-hotels-card.component';
import { SwiperService } from '@shared/services/swiper-config.service';
import { SwiperModule } from 'swiper/angular';
import { HotelInfoUiService } from '../accommodation-info/services/hotel-info-ui.service';

import { IHOBreadcrumbModule, IHOCardModule } from 'design-system';
import { PdpBreadcrumbComponent } from '../accommodation-info/sections/pdp-breadcrumb/pdp-breadcrumb.component';
import { DealsService } from './deals.service';
import { Title } from '@angular/platform-browser';
@Component({
 selector: 'iho-deals',
 templateUrl: './deals.component.html',
 styleUrls: ['./deals.component.scss'],
 standalone: true,
 providers: [HotelRepository, HotelInfoUiService],
 imports: [
  MatInputModule,
  MatTabsModule,
  MatFormFieldModule,
  MatButtonModule,
  MatButtonToggleModule,
  forwardRef(() => DesktopSearchboxComponent),
  CommonModule,
  PopularDestinationComponent,
  SimilarHotelsCardComponent,
  SwiperModule,
  IHOCardModule,
  IHOBreadcrumbModule,
  RialToTomanPipe,
  PdpBreadcrumbComponent,
 ],
})
export class DealsComponent implements OnInit {
 listBreadcrumb: any;
 csrStatus = signal<any>(false);
 resetSwiper = signal(true);
 hotelRepo = inject(HotelRepository);
 dealsService = inject(DealsService);
 route = inject(ActivatedRoute);
 title = inject(Title);
 data: any;
 breadcrumbData: any;
 cdnUrl = cdnUrl;
 autocompleteService = inject(AutocompleteService);
 datepickerService = inject(DatepickerService);
 searchBoxService = inject(SearchBoxService);
 searchDataChanged = false;
 autocompleteValidate: boolean = false;
 config: any;
 imageUrl = '';
 fallbackUrl = 'https://cdn.iranhotelonline.com/images/no-image.jpg';
 isDragging = false;

 CITY_COOKIES_KEY = makeStateKey<string>('DEALS_PROJECT_COOKIES');

 constructor(
  private swiperService: SwiperService,
  @Inject(PLATFORM_ID) public platformId: Object,
  @Inject(DOCUMENT) private document: Document,
  private transferState: TransferState,
  @Optional() @Inject(REQUEST) private request: any,
  @Optional() @Inject(RESPONSE) private response: any
 ) {
  afterNextRender(() => {
   this.resetSwiper.set(false);
   setTimeout(() => this.resetSwiper.set(true), 0);
  });

  effect(() => {
   const dealsData = this.dealsService.hotels();
   if (dealsData) {
    this.data = dealsData;
    this.title.setTitle(this.data.DealName);
    this.breadcrumbData = [
     {
      name: 'ایران هتل',
      url: '/',
     },
     {
      name: this.data.DealName,
      url: null,
     },
    ];
    if (isPlatformBrowser(this.platformId)) {
     const img = new Image();
     img.src = this.data?.HeroImage;
     img.onload = () => {
      this.imageUrl = this.data?.HeroImage;
     };
     img.onerror = () => {
      this.imageUrl = this.fallbackUrl;
     };

     this.validateHotelImages();
    } else {
     this.imageUrl = this.data?.HeroImage || this.fallbackUrl;
    }

    this.setCityProjectDumpingIdCookie();
   }
  });
 }

 private setCityProjectDumpingIdCookie(): void {
  if (!this.data?.CityProjectDumpingId) return;

  let hasCookie = false;

  if (isPlatformServer(this.platformId)) {
   const cookieHeader: string = this.request?.headers?.cookie ?? '';
   hasCookie = cookieHeader.includes('CityProjectDumpingId=');
  } else if (isPlatformBrowser(this.platformId)) {
   hasCookie = this.document.cookie.includes('CityProjectDumpingId=');
  }

  if (hasCookie) {
   return;
  }

  const cookieMaxAgeSeconds = 60 * 60 * 2; 
  const cookieStr = `CityProjectDumpingId=${encodeURIComponent(
   this.data.CityProjectDumpingId
  )}; Path=/; Max-Age=${cookieMaxAgeSeconds}; SameSite=Lax; HttpOnly`;

  if (isPlatformServer(this.platformId)) {
   if (this.response) {
    try {
     if (typeof this.response.append === 'function') {
      this.response.append('Set-Cookie', cookieStr);
     } else {
      this.response.setHeader('Set-Cookie', cookieStr);
     }
    } catch (e) {
    }
   }
   this.transferState.set(this.CITY_COOKIES_KEY, cookieStr);
   return;
  }

  if (isPlatformBrowser(this.platformId)) {
   const nonHttpOnly = cookieStr.replace(/;\s*HttpOnly/i, '');
   this.document.cookie = nonHttpOnly;

   if (this.transferState.hasKey(this.CITY_COOKIES_KEY)) {
    this.transferState.remove(this.CITY_COOKIES_KEY);
   }
  }
 }
 ngOnInit(): void {
  let config = {
   slide: 5,
   pagination: {
    enabled: false,
    clickable: false,
   },
  };
  const baseConfig = this.swiperService.SwiperConfig(config);
  this.config = {
   ...baseConfig,
   simulateTouch: true,
   touchEventsTarget: 'container',
   allowTouchMove: true,
   touchRatio: 1,
   touchAngle: 45,
   preventClicks: true,
  preventClicksPropagation: true,
   touchStartPreventDefault: true,
   touchMoveStopPropagation: false,
   resistance: true,
   resistanceRatio: 0.85,
   on: {
    touchStart: () => {
      this.isDragging = false;
    },
    touchMove: () => {
      this.isDragging = true;
    },
    touchEnd: () => {
      setTimeout(() => {
        this.isDragging = false;
      }, 100);
    },
    slideChangeTransitionStart: () => {
      this.isDragging = true;
    },
    slideChangeTransitionEnd: () => {
      setTimeout(() => {
        this.isDragging = false;
      }, 100);
    },
   },
   breakpoints: {
    ...baseConfig.breakpoints,
    320: {
     ...baseConfig.breakpoints?.[320],
     slidesPerView: 'auto',
    },
   },
  };
 }

 
 validateHotelImages(): void {
  if (!this.data?.DestinationsWithHotels) return;

  this.data.DestinationsWithHotels.forEach((destination: any) => {
   destination.Hotels?.forEach((hotel: any) => {
    if (hotel.PictureUrl) {
     const img = new Image();
     img.src = hotel.PictureUrl;
     img.onerror = () => {
      hotel.PictureUrl = this.cdnUrl + '/images/no-image.jpg';
     };
    } else {
     hotel.PictureUrl = this.cdnUrl + '/images/no-image.jpg';
    }
   });
  });
 }

  getCityId(title: string): string {
    return 'city-' + (title);
  }
}
``